
"use client"

import { useState } from "react"
import Link from "next/link"
import { motion } from "framer-motion"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Search, Calendar, BookOpen, ArrowRight } from "lucide-react"
import TextReveal from "@/components/animated/text-reveal"
import GradientBackground from "@/components/animated/gradient-background"
import Breadcrumbs from "@/components/seo/breadcrumbs"

interface Resource {
    id: number
    title: string
    slug: string
    date: string
    category: string
    excerpt: string
    meta: {
        title: string
        description: string
    }
}

export default function ResourcesPageClient({ resources }: { resources: Resource[] }) {
    const [searchTerm, setSearchTerm] = useState("")

    const filteredResources = resources.filter((resource) =>
        resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.category.toLowerCase().includes(searchTerm.toLowerCase())
    )

    return (
        <div className="min-h-screen relative">
            <GradientBackground />

            <div className="container mx-auto px-4 py-12 relative z-10">
                {/* Header */}
                <TextReveal>
                    <div className="text-center mb-12">
                        <Badge className="bg-blue-600/10 text-blue-600 border-blue-600/20 mb-4">Knowledge Hub</Badge>
                        <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6 font-display">Expert Guides & Resources</h1>
                        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                            Deepen your understanding of industrial hardware with our comprehensive guides, technical analysis, and industry best practices.
                        </p>
                    </div>
                </TextReveal>

                <Breadcrumbs items={[{ name: "Resources", url: "/resources" }]} />

                {/* Search */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="max-w-2xl mx-auto mb-16"
                >
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 h-5 w-5" />
                        <Input
                            placeholder="Search guides, categories, or topics..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-12 py-6 text-lg border-slate-200 focus:border-blue-500 bg-white/80 backdrop-blur-sm shadow-xl rounded-2xl transition-all"
                        />
                    </div>
                </motion.div>

                {/* Resources Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                    {filteredResources.map((resource, index) => (
                        <motion.div
                            key={resource.id}
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                        >
                            <Card className="h-full border-0 shadow-lg hover:shadow-2xl transition-all duration-300 bg-white/90 backdrop-blur-sm overflow-hidden group">
                                <CardHeader className="pb-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <Badge variant="secondary" className="bg-blue-50 text-blue-700">{resource.category}</Badge>
                                        <div className="flex items-center text-xs text-slate-500">
                                            <Calendar className="h-3 w-3 mr-1" />
                                            {new Date(resource.date).toLocaleDateString()}
                                        </div>
                                    </div>
                                    <CardTitle className="text-xl font-bold line-clamp-2 leading-tight group-hover:text-blue-600 transition-colors">
                                        {resource.title}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    <CardDescription className="text-slate-600 line-clamp-3 text-base">
                                        {resource.excerpt}
                                    </CardDescription>
                                    <Button variant="ghost" className="p-0 text-blue-600 hover:text-blue-700 font-semibold group" asChild>
                                        <Link href={`/resources/${resource.slug}`} className="flex items-center">
                                            Read In-Depth Guide
                                            <ArrowRight className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                                        </Link>
                                    </Button>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </div>

                {filteredResources.length === 0 && (
                    <div className="text-center py-20">
                        <BookOpen className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                        <h3 className="text-xl font-semibold text-slate-900">No resources found</h3>
                        <p className="text-slate-600 mt-2">Try adjusting your search terms or browse all categories.</p>
                    </div>
                )}

                {/* Newsletter / CTA */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="bg-slate-900 rounded-3xl p-10 text-white text-center relative overflow-hidden"
                >
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-purple-600/20" />
                    <div className="relative z-10">
                        <h2 className="text-3xl font-bold mb-4">Can't find what you're looking for?</h2>
                        <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
                            Our technical experts are available to provide custom guidance and answers for your specific project requirements.
                        </p>
                        <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100" asChild>
                            <Link href="/contact">Ask an Expert</Link>
                        </Button>
                    </div>
                </motion.div>
            </div>
        </div>
    )
}
